import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, CalendarCheck, ShieldCheck, Wallet, Star, MapPin } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import PackageCard from "@/components/PackageCard";

const HERO = "https://images.pexels.com/photos/33485972/pexels-photo-33485972.jpeg?auto=compress&cs=tinysrgb&w=1920";

export default function Home() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {});
    api.get("/testimonials").then(({ data }) => setReviews(data)).catch(() => {});
  }, []);

  const featured = packages.filter((p) => p.featured).slice(0, 3);

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#07070a] via-[#07070a]/85 to-[#07070a]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07070a] to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 w-full">
          <div
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-gold border border-gold/30 rounded-full px-4 py-2 mb-6">
              <Sparkles size={14} /> {t("hero_tag")}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[0.95] text-white tracking-tight">
              {t("hero_title")}
            </h1>
            <p className="mt-6 text-base md:text-lg text-zinc-200 max-w-xl leading-relaxed">{t("hero_sub")}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Button
                onClick={() => navigate("/packages")}
                data-testid="hero-explore-btn"
                size="lg"
                className="gold-gradient text-white font-semibold rounded-full h-13 px-8 hover:-translate-y-0.5 transition-transform"
              >
                {t("explore_packages")} <ArrowRight size={18} className="ml-1.5" />
              </Button>
              <Button
                onClick={() => navigate("/inquiry")}
                data-testid="hero-inquiry-btn"
                size="lg"
                variant="outline"
                className="rounded-full h-13 px-8 border-white/30 text-white hover:bg-white/10"
              >
                {t("wedding_inquiry")}
              </Button>
            </div>
            <p className="mt-6 inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-gold bg-white/10 border border-gold/30 rounded-full px-4 py-2" data-testid="hero-service-area">
              <MapPin size={14} className="shrink-0" /> {t("service_area")}
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 -mt-12 relative z-20">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { icon: CalendarCheck, title: "Check & Book Online", desc: "Real-time date availability" },
            { icon: Wallet, title: "Just 10% Advance", desc: "Pay securely via UPI" },
            { icon: ShieldCheck, title: "Transparent Refunds", desc: "75% refund on cancellation" },
          ].map((f, i) => (
            <div
              key={i}
              className="bg-card border border-pink-200 rounded-2xl p-6 flex items-start gap-4"
            >
              <div className="h-11 w-11 rounded-xl gold-gradient flex items-center justify-center shrink-0">
                <f.icon size={20} className="text-black" />
              </div>
              <div>
                <h3 className="text-zinc-900 font-semibold">{f.title}</h3>
                <p className="text-sm text-zinc-600 mt-1">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured packages */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-24">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase text-gold">{t("our_services")}</span>
            <h2 className="font-display text-3xl sm:text-4xl text-zinc-900 mt-2">{t("services_sub")}</h2>
          </div>
          <Link to="/packages" className="text-gold text-sm flex items-center gap-1.5 hover:gap-3 transition-all" data-testid="home-all-packages">
            View all <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {featured.map((p, i) => (
            <PackageCard key={p.id} pkg={p} index={i} />
          ))}
        </div>
      </section>

      {/* Testimonials */}
      {reviews.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-24" data-testid="testimonials-section">
          <div className="text-center mb-10">
            <span className="text-xs tracking-[0.2em] uppercase text-gold">Happy Clients</span>
            <h2 className="font-display text-3xl sm:text-4xl text-zinc-900 mt-2">What Our Customers Say</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {reviews.slice(0, 6).map((r) => (
              <div key={r.id} className="bg-card border border-pink-200 rounded-2xl p-6 flex flex-col" data-testid={`review-${r.id}`}>
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={16} className={i < (r.rating || 5) ? "text-gold fill-current" : "text-pink-200"} />
                  ))}
                </div>
                <p className="text-zinc-700 text-sm leading-relaxed flex-1">“{r.message}”</p>
                {r.reply && (
                  <div className="mt-4 bg-pink-50 border-l-2 border-gold rounded-r-lg px-3 py-2" data-testid={`review-reply-${r.id}`}>
                    <div className="text-[11px] font-semibold text-gold uppercase tracking-wide mb-0.5">Metrix Team</div>
                    <p className="text-xs text-zinc-600 leading-relaxed">{r.reply}</p>
                  </div>
                )}
                <div className="flex items-center gap-3 mt-5 pt-4 border-t border-pink-100">
                  {(r.photo_url || r.photo_path) && (
                    <img src={r.photo_url || fileUrl(r.photo_path)} alt={r.name} className="h-11 w-11 rounded-full object-cover border border-gold/40" loading="lazy" />
                  )}
                  <div>
                    <div className="text-zinc-900 font-semibold text-sm">{r.name}</div>
                    <div className="text-xs text-gold">{r.event_type}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-24">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#EC4899] via-[#DB2777] to-[#9D174D] border border-pink-300 p-10 md:p-16 text-center grain">
          <div className="relative z-10">
            <h2 className="font-display text-3xl md:text-5xl text-white">Planning a Grand Wedding?</h2>
            <p className="text-pink-50 mt-4 max-w-xl mx-auto">Flower showers, cold fire, varmala entries & fully custom setups. Let's craft something unforgettable.</p>
            <Button onClick={() => navigate("/inquiry")} size="lg" data-testid="cta-inquiry-btn" className="mt-8 bg-white text-pink-700 hover:bg-pink-50 font-semibold rounded-full px-8">
              {t("wedding_inquiry")} <ArrowRight size={18} className="ml-1.5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
